import { useCallback, useMemo } from "react";
import { useCsAttributes, useCsCfgObject, useCsConfigurationApi, } from "@plansoft-configuration-renderer/framework";
import { PluginHelper, } from "@plansoft-configuration-renderer/layout";
import { useConfigurationAttributeGroupTranslators } from "@plansoft-configuration-renderer/localization";
import { AttributeBaseType } from "../../AttributeComponents/AttributeBaseType";
import { composeGroupPath } from "../../NavigationState/composeGroupPath";
import { useAttributeGroupHelpers, } from "../AttributeGroup";
import { ConfigurationComponentType } from "../ConfigurationComponentType";
import { getNestedSubstructureLayoutPropsWithFallback } from "../shared/getNestedSubstructureLayoutProps";
import { shouldShowIndexInSubstructure } from "../shared/shouldShowIndexInSubstructure";
import { SubconfigurationNavigationItemType, } from "./SubconfigurationNavigationProps";
import { isSubconfigurationRenderedAsInstances } from "./isSubconfigurationRenderedAsInstances";
import { useAttributeOrGroupLayoutCells } from "./useAttributeOrGroupLayoutCells";
export function useSubconfigurationNavigationItems(_options) {
    const cells = useAttributeOrGroupLayoutCells();
    const attributes = useCsAttributes();
    const cfgObject = useCsCfgObject();
    const { resetPath, resetAttributes } = useCsConfigurationApi();
    const translateGroup = useConfigurationAttributeGroupTranslators();
    const { getIsGroupComplete, resetGroup } = useAttributeGroupHelpers();
    const makeAttributeGroupItem = useCallback((childCell) => {
        var _a, _b, _c;
        const { title: groupId, groupBehavior, custom, } = PluginHelper.getPluginProps(childCell);
        const groupOptions = {
            groupId,
            groupBehavior,
            groupLayoutCellId: childCell.id,
        };
        const group = (_b = (_a = cfgObject.attributeGroups) === null || _a === void 0 ? void 0 : _a.find((g) => g.id === groupId)) !== null && _b !== void 0 ? _b : {
            id: groupId,
            name: translateGroup.name(groupId),
            description: translateGroup.description(groupId),
        };
        return [
            {
                type: SubconfigurationNavigationItemType.Group,
                group,
                layoutCellId: childCell.id,
                path: composeGroupPath({
                    configurationObjectPath: cfgObject.path,
                    groupId: groupId,
                    cellId: childCell === null || childCell === void 0 ? void 0 : childCell.id,
                }),
                id: groupId,
                label: (_c = group.name) !== null && _c !== void 0 ? _c : groupId,
                name: group.name,
                description: group.description,
                isComplete: getIsGroupComplete(groupOptions),
                custom,
                reset: () => {
                    resetGroup(groupOptions);
                },
            },
        ];
    }, [
        cfgObject.path,
        cfgObject.attributeGroups,
        getIsGroupComplete,
        resetGroup,
        translateGroup,
    ]);
    const makeAttributeItems = useCallback((childCell) => {
        var _a;
        const { identifier, custom } = PluginHelper.getPluginProps(childCell);
        const attribute = attributes.find((attribute) => attribute.id === identifier);
        if (!attribute) {
            return [];
        }
        return [
            {
                type: SubconfigurationNavigationItemType.Attribute,
                attribute,
                layoutCellId: childCell.id,
                path: attribute.path,
                label: (_a = attribute.name) !== null && _a !== void 0 ? _a : attribute.id,
                id: attribute.id,
                name: attribute.name,
                description: attribute.description,
                isComplete: attribute.isComplete,
                custom,
                reset: () => {
                    resetAttributes([
                        {
                            attributePath: attribute.path,
                            attributeId: attribute.id,
                        },
                    ]);
                },
            },
        ];
    }, [attributes, resetAttributes]);
    const makeSubstructureInstanceItems = useCallback((cell) => {
        const { identifier, custom } = PluginHelper.getPluginProps(cell);
        const attribute = attributes.find((attribute) => attribute.id === identifier);
        if (!(attribute === null || attribute === void 0 ? void 0 : attribute.configurations)) {
            return [];
        }
        const { showIndexInLabel = shouldShowIndexInSubstructure(attribute) } = getNestedSubstructureLayoutPropsWithFallback(cell);
        return attribute.configurations.map((configuration) => {
            var _a;
            const name = (_a = configuration.name) !== null && _a !== void 0 ? _a : configuration.id;
            return {
                type: SubconfigurationNavigationItemType.Instance,
                attribute,
                instance: configuration,
                layoutCellId: cell.id,
                path: configuration.path,
                id: configuration.id,
                label: showIndexInLabel ? `${name} ${configuration.index + 1}` : name,
                name: configuration.name,
                description: configuration.description,
                isComplete: configuration.isComplete,
                custom,
                reset: () => {
                    resetPath(configuration.path);
                },
            };
        });
    }, [attributes, resetPath]);
    const makeSubstructureItems = useCallback((cell) => {
        if (isSubconfigurationRenderedAsInstances(cell)) {
            return makeSubstructureInstanceItems(cell);
        }
        return makeAttributeItems(cell);
    }, [makeAttributeItems, makeSubstructureInstanceItems]);
    // extract the plugin data from the layout
    return useMemo(() => cells.flatMap((cell) => {
        const pluginId = PluginHelper.getPluginId(cell);
        if (pluginId === AttributeBaseType.Substructure) {
            return makeSubstructureItems(cell);
        }
        if (Object.values(AttributeBaseType).includes(pluginId)) {
            return makeAttributeItems(cell);
        }
        if (pluginId === ConfigurationComponentType.AttributeGroup) {
            return makeAttributeGroupItem(cell);
        }
        // fallback: no items
        return [];
    }), [cells, makeAttributeGroupItem, makeAttributeItems, makeSubstructureItems]);
}
//# sourceMappingURL=useSubconfigurationNavigationItems.js.map